import { msg } from '@lingui/core/macro';
import { BoardPresets, boardPresets } from 'modules/template-editor/domain/consts/form/boards/boardPresets';
import { Values } from 'modules/template-editor/domain/interface/form/board';
import boardSettingsFromValues from 'modules/template-editor/domain/utils/form/board/board-settings-from-values';
import { getEventsOptions, getTypeOptions } from 'modules/template-editor/ui/components/elements/components/events-selector/type';
import BoardForm from 'modules/template-editor/ui/components/elements/forms/board-form';
import { useLingui } from '@lingui/react/macro';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDispatchWithToast } from 'store/use-dispatch-with-toast';

import { AppDispatch } from 'store/store';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import { copyBoard } from 'modules/template-editor/store/templates/actions';
import { resetPressedState } from 'modules/template-editor/store/templates/templates';
import useBoards from 'modules/template-editor/ui/hooks/use-boards';

const PRESET = BoardPresets.duplicate;

function useCopyModal() {
  const { t } = useLingui();
  const dispatch = useDispatch<AppDispatch>();
  const dispatchWithToast = useDispatchWithToast();
  const { activeBoard, boards, isProcessed, isFinished, process } = useBoards();

  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  const onSubmit = useCallback(
    // `async` + промис запроса — Formik держит кнопку формы запертой до ответа (IMGM-1561).
    async (values: Values) => {
      if (!activeBoard) return;

      return dispatchWithToast(
        copyBoard({
          ...boardSettingsFromValues(values, activeBoard.isFavorite),
          boardId: activeBoard.id,
          templateId: activeBoard.templateId,
          process: 'copy board',
        }),
        { error: msg`Не удалось создать копию` },
      );
    },
    [activeBoard, dispatchWithToast],
  );

  const initialValue = useMemo(() => {
    const events = activeBoard
      ? (getEventsOptions().filter((option) => activeBoard?.events?.includes(option.value)) as Values['events'])
      : undefined;

    return {
      // eslint-disable-next-line lingui/no-unlocalized-strings -- имя копии уходит в базу, это данные, а не подпись интерфейса
      name: activeBoard?.name ? activeBoard?.name + ' (дубликат)' : '',
      type: getTypeOptions().find((type) => type.value === activeBoard?.type),
      events: events,
      duration: activeBoard?.duration?.toString(),
    } as Values;
  }, [activeBoard]);

  const existingNames = useMemo(() => boards.map((board) => board.name.trim()), [boards]);

  useEffect(() => {
    if (!isFinished) {
      return;
    }
    if (process !== 'copy board') {
      return;
    }
    close();
    dispatch(resetPressedState());
    if (!activeBoard?.templateId) {
      return;
    }
  }, [activeBoard?.templateId, close, dispatch, isFinished, process]);

  return {
    modal: (
      <Modal show={isOpen} onClose={close} size="md">
        <UDText type={'title-3'} className="mb-5">
          {t(boardPresets[PRESET].header)}
        </UDText>
        <BoardForm
          onSubmit={onSubmit}
          initialValues={initialValue}
          isLoading={isProcessed}
          preset={PRESET}
          existingNames={existingNames}
          disableImport
        />
      </Modal>
    ),
    close,
    open,
  };
}

export default useCopyModal;
